// PWA plumbing: capture the install prompt before React mounts (Chrome fires it early), register the service worker in production builds only,
// and keep an installed app fresh — an installed PWA never "navigates", so the browser's own update check would otherwise never run.
let deferred = null;
const listeners = new Set();
const updateListeners = new Set();
let registration = null;
let baselineBuild = null;      // build id this page loaded with (from /version.json)
let latestBuild = null;        // newest build id seen on the server
let updateReady = false;       // a newer build is installed/available and a reload will pick it up
let lastCheck = 0;
const CHECK_EVERY_MS = 30 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;

const notifyUpdate = () => updateListeners.forEach((fn) => fn(updateReady));

async function fetchBuild() {
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return null;
    const j = await r.json();
    return j.build || null;
  } catch { return null; }
}

function markReady() {
  if (updateReady) return;
  updateReady = true;
  notifyUpdate();
}

function watchWorker(w) {
  if (!w) return;
  w.addEventListener("statechange", () => { if (w.state === "installed" || w.state === "activated") markReady(); });
}

// Check both signals: the service worker script and the build id. Returns "update" | "current" | "unknown".
export async function checkForUpdate({ force = false } = {}) {
  const now = Date.now();
  if (!force && now - lastCheck < MIN_GAP_MS) return updateReady ? "update" : "current";
  lastCheck = now;
  try {
    if (registration) {
      await registration.update();
      if (registration.waiting) markReady();
    }
  } catch { /* offline or blocked — the build check below still runs */ }
  const build = await fetchBuild();
  if (build) {
    latestBuild = build;
    if (!baselineBuild) baselineBuild = build;
    else if (build !== baselineBuild) markReady();
  }
  if (updateReady) return "update";
  return build || registration ? "current" : "unknown";
}

// Activate the waiting worker (if any) and reload so the new bundle is what's running.
export function applyUpdate() {
  const w = registration?.waiting;
  if (w) {
    let reloaded = false;
    navigator.serviceWorker?.addEventListener("controllerchange", () => { if (!reloaded) { reloaded = true; window.location.reload(); } });
    w.postMessage("SKIP_WAITING");
    setTimeout(() => { if (!reloaded) { reloaded = true; window.location.reload(); } }, 1500);
    return;
  }
  window.location.reload();
}

const typing = () => {
  const el = document.activeElement;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; listeners.forEach((fn) => fn()); });
  window.addEventListener("appinstalled", () => { deferred = null; listeners.forEach((fn) => fn()); });
  if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((reg) => {
        registration = reg;
        if (reg.waiting) markReady();
        watchWorker(reg.installing);
        reg.addEventListener("updatefound", () => watchWorker(reg.installing));
      }).catch(() => {});
    });
  }
  // Baseline the build id, then re-check when the app is resumed, and on a timer while it stays open.
  window.addEventListener("load", () => { fetchBuild().then((b) => { baselineBuild = b; latestBuild = b; }); });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    checkForUpdate().then((r) => {
      // Resumed from the background with an update already waiting and nobody mid-typing: refresh quietly — this is the moment a relaunch would have happened anyway.
      if (r === "update" && !typing()) applyUpdate();
    });
  });
  setInterval(() => checkForUpdate(), CHECK_EVERY_MS);
}

export const isStandalone = () => typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
export const isIOS = () => typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
export const canPrompt = () => !!deferred;
export const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const subscribeUpdate = (fn) => { updateListeners.add(fn); if (updateReady) fn(true); return () => updateListeners.delete(fn); };
export const isUpdateReady = () => updateReady;
export const currentBuild = () => baselineBuild;
export const newestBuild = () => latestBuild;
export async function promptInstall() {
  if (!deferred) return "unavailable";
  const ev = deferred;
  ev.prompt();
  const { outcome } = await ev.userChoice;
  if (outcome === "accepted") deferred = null;
  listeners.forEach((fn) => fn());
  return outcome;
}
